import dayjs, { setDayjsLocale } from '@/utils/dayjs';
import {
  formatTimezoneOffset,
  getDisplayTimezone,
} from '@/utils/displayTimezone';
import { getLanguageCode } from '@/utils/i18nLocale';

import type { Dayjs } from 'dayjs';

type DateInput = string | Date | Dayjs | null | undefined;

const EMPTY = '—';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

type ParsedDate = {
  value: Dayjs;
  /** Absolute instants get a zone label on clock patterns. Picker wall times do not. */
  labelZone: boolean;
};

const isAbsoluteInstant = (value: DateInput): boolean => {
  if (value instanceof Date) {
    return true;
  }
  if (typeof value === 'string') {
    return !DATE_ONLY.test(value) && /\d{2}:\d{2}/.test(value);
  }
  return false;
};

const toDayjs = (value: DateInput, language?: string): ParsedDate | null => {
  if (value == null || value === '') {
    return null;
  }
  setDayjsLocale(language);

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? { value, labelZone: false } : null;
  }

  const zone = getDisplayTimezone();
  const labelZone = isAbsoluteInstant(value);
  const parsed = zone && labelZone ? dayjs(value).tz(zone) : dayjs(value);
  return parsed.isValid() ? { value: parsed, labelZone } : null;
};

const formatPattern = (
  value: DateInput,
  pattern: string,
  language?: string,
  fallback: string = EMPTY,
): string => {
  const parsed = toDayjs(value, language);
  if (!parsed) {
    return fallback === '' ? '' : fallback;
  }
  const formatted = parsed.value.format(pattern);
  if (!parsed.labelZone || !pattern.includes('H')) {
    return formatted;
  }
  const offset = formatTimezoneOffset(parsed.value.toDate());
  return offset ? `${formatted} ${offset}` : formatted;
};

/** MMM D, YYYY */
export const formatShortDate = (value: DateInput, language?: string): string =>
  formatPattern(value, 'MMM D, YYYY', language);

/** MMMM D, YYYY */
export const formatLongDate = (value: DateInput, language?: string): string =>
  formatPattern(value, 'MMMM D, YYYY', language);

/** MMM D, YYYY [at] HH:mm */
export const formatDateTime = (value: DateInput, language?: string): string =>
  formatPattern(value, 'MMM D, YYYY [at] HH:mm', language);

/** HH:mm */
export const formatTime = (value: DateInput, language?: string): string =>
  formatPattern(value, 'HH:mm', language);

/** YYYY-MM-DD */
export const formatApiDate = (value: DateInput, language?: string): string =>
  formatPattern(value, 'YYYY-MM-DD', language, '');

/** MMM D, H:mm — compact date + time */
export const formatShortDateTime = (
  value: DateInput,
  language?: string,
): string => formatPattern(value, 'MMM D, HH:mm', language);

/** MMMM d, EEEE — calendar event header */
export const formatCalendarEventTitle = (
  value: DateInput,
  language?: string,
): string => formatPattern(value, 'MMMM D, dddd', language);

/** MMMM D — month day for calendar cells */
/** Chart axis: month short + year */
export const formatChartMonthYear = (
  value: DateInput,
  language?: string,
): string => formatPattern(value, 'MMM YYYY', language);

/** Chart axis: calendar quarter + year */
export const formatChartQuarter = (
  value: DateInput,
  language?: string,
): string => formatPattern(value, '[Q]Q YYYY', language);

/** Chart axis: year */
export const formatChartYear = (value: DateInput, language?: string): string =>
  formatPattern(value, 'YYYY', language);

/** Calendar header: full month + year */
export const formatCalendarMonthYear = (
  value: DateInput,
  language?: string,
): string => formatPattern(value, 'MMMM YYYY', language);

/** Chart axis: month short + day */
export const formatChartMonthDay = (
  value: DateInput,
  language?: string,
): string => formatPattern(value, 'MMM D', language);

/** Message day-group header: weekday + short month day */
export const formatMessageDayGroup = (
  value: DateInput,
  language?: string,
): string => formatPattern(value, 'dddd, MMM D', language);

/** Interview schedule: ordinal day + month + year + time */
export const formatInterviewSchedule = (
  value: DateInput,
  language?: string,
): string => formatPattern(value, 'Do [of] MMMM YYYY HH:mm', language);

type MessageTimestampLabels = {
  today: string;
  yesterday: string;
};

/**
 * Unified message/chat timestamp: today/yesterday show label + time;
 * this week shows weekday + time; older shows short date + time.
 */
export const formatMessageTimestamp = (
  value: DateInput,
  labels: MessageTimestampLabels,
  language?: string,
): string => {
  const parsed = toDayjs(value, language);
  if (!parsed) {
    return EMPTY;
  }
  const d = parsed.value;
  const zone = getDisplayTimezone();
  const now = zone ? dayjs().tz(zone) : dayjs();
  if (d.isSame(now, 'day')) {
    return `${labels.today}${formatPattern(value, ', HH:mm', language)}`;
  }
  if (d.isSame(now.subtract(1, 'day'), 'day')) {
    return `${labels.yesterday}${formatPattern(value, ', HH:mm', language)}`;
  }
  if (d.isSame(now, 'week')) {
    return formatPattern(value, 'dddd, HH:mm', language);
  }
  return formatShortDateTime(value, language);
};

/** Relative to now (e.g. "3 hours ago") — notifications */
export const formatRelativeToNow = (
  value: DateInput,
  language?: string,
  options?: { addSuffix?: boolean },
): string => {
  const parsed = toDayjs(value, language);
  if (!parsed) {
    return EMPTY;
  }
  const d = parsed.value;
  const addSuffix = options?.addSuffix ?? true;
  // dayjs takes `withoutSuffix`, the inverse of our option.
  return d.fromNow(!addSuffix);
};

const screeningDatePickerFormats: Record<string, string> = {
  nl: 'DD-MM-YYYY',
  fr: 'DD/MM/YYYY',
  de: 'DD.MM.YYYY',
  es: 'DD/MM/YYYY',
  ja: 'YYYY/MM/DD',
  it: 'DD/MM/YYYY',
  en: 'MM/DD/YYYY',
};

/** Locale-specific DatePicker display pattern for screening question forms */
export const getScreeningDatePickerFormat = (language?: string): string =>
  screeningDatePickerFormats[getLanguageCode(language)] ?? 'DD/MM/YYYY';

type ScheduledLabelOptions = {
  withTime?: boolean;
};

/** Marketplace / toolbar scheduled-access labels */
export const formatScheduledLabel = (
  value: DateInput,
  language?: string,
  options?: ScheduledLabelOptions,
): string => {
  if (options?.withTime) {
    return formatDateTime(value, language);
  }
  return formatShortDate(value, language);
};
